/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import LanguageLink from "../routing/LanguageLink";
import { motion } from "framer-motion";
import ScrollAnimation from "../../hooks/ScrollAnimation";
import heroImage from "../../assets/mig2.jpg";
import { useTranslation } from "react-i18next";

const HeroSection = () => {
  const { t } = useTranslation();
  const services = t("hero.typing_items", { returnObjects: true }) || [
    "Form Assistance",
    "Lawyer Matching",
    "Payment Processing",
    "Legal Consultation",
    "Document Review",
    "Case Tracking",
    "Immigration Support",
    "Legal Representation",
    "Online Consultation",
    "Green Card Applications",
    "Work Visas",
  ];

  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(60);

  useEffect(() => {
    const currentService = services[currentServiceIndex];

    if (!isDeleting && displayedText === currentService) {
      const timeout = setTimeout(() => {
        setIsDeleting(true);
        setTypingSpeed(30);
      }, 1500);
      return () => clearTimeout(timeout);
    } else if (isDeleting && displayedText === "") {
      setIsDeleting(false);
      setCurrentServiceIndex((prev) => (prev + 1) % services.length);
      setTypingSpeed(60);
      return;
    } else {
      const timeout = setTimeout(() => {
        if (isDeleting) {
          setDisplayedText(currentService.substring(0, displayedText.length - 1));
        } else {
          setDisplayedText(currentService.substring(0, displayedText.length + 1));
        }
      }, typingSpeed);

      return () => clearTimeout(timeout);
    }
  }, [displayedText, isDeleting, currentServiceIndex, services]);

  const features = [
    { icon: "⚡", text: t("hero.features.support") },
    { icon: "✓", text: t("hero.features.guidance") },
    { icon: "🔒", text: t("hero.features.secure") },
    { icon: "💯", text: t("hero.features.quality") },
  ];

  const stats = [
    { number: t("hero.stats.clients.number"), label: t("hero.stats.clients.label") },
    { number: t("hero.stats.satisfaction.number"), label: t("hero.stats.satisfaction.label") },
    { number: t("hero.stats.experience.number"), label: t("hero.stats.experience.label") },
  ];

  return (
    <section className="relative bg-gradient-to-br from-white via-blue-50 to-white py-[60px] overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute -bottom-20 left-10 w-72 h-72 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
          {/* Left Section - Text Content */}
          <ScrollAnimation direction="left" delay={0.2}>
            <div className="space-y-3">
              {/* Badge */}
              <motion.div
                className="inline-flex items-center space-x-2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-600"></span>
                </span>
                <span className="text-blue-600 font-semibold text-sm uppercase tracking-wide">
                  <span className="inline-block min-w-[180px] sm:min-w-[240px] text-left">
                    {displayedText}
                    <span className="inline-block w-0.5 h-4 bg-blue-600 ml-1 animate-pulse align-middle">|</span>
                  </span>
                </span>
              </motion.div>

              {/* Main Heading */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                <h1 className="text-2xl lg:text-3xl xl:text-4xl font-extrabold leading-tight">
                  <span className="text-gray-900">{t("hero.title.line1")}</span>
                  <br />
                  <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 bg-clip-text text-transparent">
                    {t("hero.title.line2")}
                  </span>
                  <br />
                  <span className="text-gray-900">{t("hero.title.line3")}</span>
                </h1>
              </motion.div>

              {/* Description */}
              <motion.p
                className="text-sm lg:text-base text-gray-600 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                {t("hero.description")}
              </motion.p>

              {/* Features Grid */}
              <motion.div
                className="grid grid-cols-2 gap-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-2 bg-white rounded-lg px-2.5 py-1.5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200"
                  >
                    <span className="text-lg">{feature.icon}</span>
                    <span className="text-gray-700 font-medium text-xs">
                      {feature.text}
                    </span>
                  </div>
                ))}
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                className="flex flex-col sm:flex-row gap-2 pt-1"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.6 }}
              >
                <LanguageLink
                  to="/register"
                  className="group relative inline-flex items-center justify-center px-5 py-2.5 text-sm font-bold text-white bg-blue-600 rounded-full overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  <span className="absolute w-0 h-0 transition-all duration-500 ease-out bg-indigo-700 rounded-full group-hover:w-full group-hover:h-full"></span>
                  <span className="relative flex items-center">
                    {t("hero.cta.get_started")}
                    <svg
                      className="w-3.5 h-3.5 ml-2 group-hover:translate-x-1 transition-transform"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                </LanguageLink>
                <LanguageLink
                  to="/forms"
                  className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-blue-600 bg-white border-2 border-blue-600 rounded-full hover:bg-blue-50 transition-all duration-300 shadow-sm hover:shadow-md"
                >
                  {t("hero.cta.browse_forms")}
                </LanguageLink>
              </motion.div>

              {/* Stats */}
              <motion.div
                className="flex flex-wrap gap-4 pt-3 border-t border-gray-200"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.6 }}
              >
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-xl font-bold text-blue-600">
                      {stat.number}
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </ScrollAnimation>

          {/* Right Section - Hero Image with Cards */}
          <ScrollAnimation direction="right" delay={0.4}>
            <div className="relative">
              {/* Main Image */}
              <motion.div
                className="relative rounded-2xl overflow-hidden shadow-xl"
                initial={{ opacity: 0, scale: 0.9, rotate: -5 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
              >
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-transparent z-10"></div>
                <img
                  src={heroImage}
                  alt="Immigration Services Assistance"
                  className="w-full h-[350px] lg:h-[400px] object-cover"
                />
              </motion.div>

              {/* Floating Card 1 - Top Right */}
              <motion.div
                className="absolute -top-3 -right-3 bg-white rounded-xl shadow-lg p-3 border border-gray-100"
                initial={{ opacity: 0, x: 20, y: -20 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ delay: 0.9, duration: 0.6 }}
              >
                <div className="flex items-center space-x-2.5">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t("hero.cards.quick_processing.label")}</p>
                    <p className="font-bold text-sm text-gray-900">{t("hero.cards.quick_processing.value")}</p>
                  </div>
                </div>
              </motion.div>

              {/* Floating Card 2 - Bottom Left */}
              <motion.div
                className="absolute -bottom-3 -left-3 bg-white rounded-xl shadow-lg p-3 border border-gray-100"
                initial={{ opacity: 0, x: -20, y: 20 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ delay: 1, duration: 0.6 }}
              >
                <div className="flex items-center space-x-2.5">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t("hero.cards.expert_team.label")}</p>
                    <p className="font-bold text-sm text-gray-900">{t("hero.cards.expert_team.value")}</p>
                  </div>
                </div>
              </motion.div>

              {/* Decorative Elements */}
              <div className="absolute top-1/2 -left-8 w-16 h-16 bg-blue-200 rounded-full filter blur-xl opacity-50"></div>
              <div className="absolute bottom-1/4 -right-8 w-20 h-20 bg-indigo-200 rounded-full filter blur-xl opacity-50"></div>
            </div>
          </ScrollAnimation>
        </div>
      </div>

      {/* Wave Separator */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg className="w-full h-12 fill-current text-white" viewBox="0 0 1440 74" preserveAspectRatio="none">
          <path d="M0,32L60,37.3C120,43,240,53,360,48C480,43,600,21,720,21.3C840,21,960,43,1080,48C1200,53,1320,43,1380,37.3L1440,32L1440,74L1380,74C1320,74,1200,74,1080,74C960,74,840,74,720,74C600,74,480,74,360,74C240,74,120,74,60,74L0,74Z"></path>
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
